import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { captureError } from "@/lib/errorReporting";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8080";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AppConfig {
    DEPLOYMENT_ENV: "devnet" | "staging" | "production";
    MIN_DEPOSIT_SOL: number;
    MAX_DEPOSIT_SOL: number;
    DEPOSIT_TARGET_SOL: number;
    INITIAL_MC_SOL: number;
    MIGRATION_MC_SOL: number;
    UNLOCK_MCAP_SOL: number;
    FEE_RESERVE_SOL: number;
    FEE_TEAM_SHARE: number;
    FEE_DEPOSITOR_SHARE: number;
    ANONCOIN_BASE_URL: string;
    TOTAL_TOKEN_SUPPLY: number;
    TOKEN_DECIMALS: number;
    SOL_DECIMALS: number;
}

// ─── Context ────────────────────────────────────────────────────────────────

const ConfigContext = createContext<AppConfig | null>(null);

/**
 * Access the app config. Must be used within <ConfigProvider>.
 * Throws if config hasn't loaded yet (provider shows loading state, so this
 * should never happen in practice).
 */
export function useConfig(): AppConfig {
    const ctx = useContext(ConfigContext);
    if (!ctx) throw new Error("useConfig() called outside <ConfigProvider>");
    return ctx;
}

// ─── Provider ───────────────────────────────────────────────────────────────

export function ConfigProvider({ children }: { children: ReactNode }) {
    const [config, setConfig] = useState<AppConfig | null>(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        fetch(`${API_BASE}/api/config`)
            .then((res) => {
                if (!res.ok) throw new Error(`Config fetch failed: ${res.status}`);
                return res.json();
            })
            .then(setConfig)
            .catch((err) => {
                captureError("Failed to fetch app config", err);
                setError(true);
            });
    }, []);

    if (error) {
        return (
            <div style={{ color: "#fff", textAlign: "center", padding: "4rem 1rem" }}>
                <p>Failed to load app configuration.</p>
                <button
                    onClick={() => window.location.reload()}
                    style={{ marginTop: "1rem", padding: "0.5rem 1.5rem", borderRadius: 8, background: "#fff", color: "#111" }}
                >
                    Retry
                </button>
            </div>
        );
    }

    if (!config) {
        // Minimal loading state — config fetch is fast (<100ms same-origin)
        return null;
    }

    return (
        <ConfigContext.Provider value={config}>
            {children}
        </ConfigContext.Provider>
    );
}
